import type { Command } from "../document/commands.ts";
import { doorAt, doorState, foldDoors, MAX_DOORS_PER_LAYER, type DoorCellChange } from "../document/doors.ts";
import { COLLISION_CELLS_PER_TILE, type DoorDefinition, type DoorLayer, type DoorTarget, type JsonValue, type LayerDocument, type MapDocument, type WorldDocument } from "../document/types.ts";
import { activationCategoryStyle, type ActivationCategoryStyle } from "./activation-categories.ts";
import { hasWayBack } from "./door-links.ts";
import type { PresenceArea } from "./presence-area.ts";
import { zoneAreas } from "./zone-overlay.ts";
import type { JsonSchema } from "./schema.ts";
import { wayBackCommand, type WayBackSide } from "./way-back.ts";

/**
 * Door authoring (ADR-122 decisions 2.1, 2.4 and 9): the door in hand, painting and erasing a door layer's cells as
 * one command, a new door, a door's name, destination and fields, a door's removal with its paint, and the way back
 * from a painted door. The cells a stroke covers are the zone brushes' (`ZoneAuthoring.stamp` and `span`); only the
 * value differs, a door id or none.
 */

/**
 * What door authoring needs from the store.
 */
export interface DoorHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly doorFields: () => JsonSchema | undefined;
  readonly arrivalType: () => string | undefined;
  readonly activeLayer: () => LayerDocument | undefined;
  readonly allocateId: (prefix: string) => string;
  readonly idMark: () => number;
  readonly rewindIds: (mark: number) => void;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * One connected area of the Doors overlay: the door painted there, its number among the map's doors and the style
 * that number draws in, and its cells.
 */
export interface DoorOverlayArea {
  readonly doorId: string;
  readonly number: number;
  readonly style: ActivationCategoryStyle;
  readonly cells: PresenceArea;
  readonly arrow: WayBackSide;
}

/**
 * The Doors overlay of a map: every connected area of one door, and a line for each door of the map in the order of
 * its door layers.
 */
export interface DoorOverlay {
  readonly areas: readonly DoorOverlayArea[];
  readonly legend: readonly { readonly doorId: string; readonly name: string; readonly number: number; readonly style: ActivationCategoryStyle }[];
}

/**
 * What a new door is made from: its name, where it leads, and the values of the game's door fields it sets (the
 * rest take the schema's defaults).
 */
export interface NewDoor {
  readonly name: string;
  readonly to: DoorTarget;
  readonly fields?: { readonly [key: string]: JsonValue };
}

/**
 * What the way back is made from, as the editor asks it: the sides, and the new arrival point's fields.
 */
export interface WayBackChoice {
  readonly outside: WayBackSide;
  readonly behind: WayBackSide;
  readonly arrivalFields: { readonly [key: string]: JsonValue };
  readonly doorName: string;
}

/**
 * The values the schema's defaults give a door.
 *
 * @param schema The game's door fields, or undefined when it registers none.
 * @returns The defaults, by field.
 */
export function doorDefaults(schema: JsonSchema | undefined): { [key: string]: JsonValue } {
  const out: { [key: string]: JsonValue } = {};
  for (const [name, property] of Object.entries(schema?.properties ?? {})) {
    if ("default" in property && property.default !== undefined) {
      out[name] = property.default;
    }
  }
  return out;
}

/**
 * The door author.
 */
export class DoorAuthoring {
  private chosen: { layerId: string; doorId: string } | null = null;
  private drawnFrom: MapDocument | null = null;
  private drawn: DoorOverlay = { areas: [], legend: [] };

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: DoorHost) {}

  /**
   * The door layer of the map in view that is active, or null.
   *
   * @returns The layer.
   */
  public get layer(): DoorLayer | null {
    const layer = this.host.activeLayer();
    return layer?.kind === "door" ? layer : null;
  }

  /**
   * The schema of the game's door settings.
   *
   * @returns The schema, or undefined when the game registers none.
   */
  public get fieldsSchema(): JsonSchema | undefined {
    return this.host.doorFields();
  }

  /**
   * The values the schema's defaults give a door of this game.
   *
   * @returns The defaults, by field.
   */
  public get defaults(): { [key: string]: JsonValue } {
    return doorDefaults(this.host.doorFields());
  }

  /**
   * The entity type the game treats as an arrival point.
   *
   * @returns The type, or undefined when the game names none.
   */
  public get arrivalType(): string | undefined {
    return this.host.arrivalType();
  }

  /**
   * Whether the map draws the Doors overlay: while a door layer is active.
   *
   * @returns True while drawn.
   */
  public get shown(): boolean {
    return this.layer !== null;
  }

  /**
   * The Doors overlay of the map in view: its painted doors in connected areas, each in the style of its number
   * among the map's doors, which is the same on every redraw. The same object until the map changes.
   *
   * @returns The overlay.
   */
  public overlay(): DoorOverlay {
    const map = this.host.map();
    if (map === this.drawnFrom) {
      return this.drawn;
    }
    const all = map.layers.flatMap((layer) => (layer.kind === "door" ? layer.doors : []));
    const folded = foldDoors(map);
    const width = map.width * COLLISION_CELLS_PER_TILE;
    const areas = zoneAreas({ types: folded.doors.map((door) => door.id), cells: folded.cells }, width, map.height * COLLISION_CELLS_PER_TILE).map((area) => {
      const number = all.findIndex((door) => door.id === area.type) + 1;
      const door = all[number - 1];
      return { doorId: area.type, number, style: activationCategoryStyle(number), cells: area.cells, arrow: door === undefined ? ("down" as const) : this.arrowOf(door) };
    });
    this.drawnFrom = map;
    this.drawn = { areas, legend: all.map((door, i) => ({ doorId: door.id, name: door.name, number: i + 1, style: activationCategoryStyle(i + 1) })) };
    return this.drawn;
  }

  /**
   * The door a collision cell of the map in view holds, across its door layers.
   *
   * @param index The cell's index in the collision grid.
   * @returns The door, or null for a cell holding none or an index off the grid.
   */
  public doorAtCell(index: number): DoorDefinition | null {
    const folded = foldDoors(this.host.map());
    return folded.doors[(folded.cells[index] ?? 0) - 1] ?? null;
  }

  /**
   * The way an arrow on a door points: the way a character faces on arriving, the `facing` field of the arrival
   * point the door leads to, and down when the arrival has none or the door's destination is missing.
   *
   * @param door The door.
   * @returns The side.
   */
  private arrowOf(door: DoorDefinition): WayBackSide {
    const target = this.host.document().maps.find((map) => map.id === door.to.map);
    const entity = target?.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities : [])).find((candidate) => candidate.id === door.to.arrival);
    const facing = entity?.fields["facing"];
    return facing === "up" || facing === "down" || facing === "left" || facing === "right" ? facing : "down";
  }

  /**
   * The door in hand: the door chosen in the active door layer, which the next stroke paints. View state outside
   * history.
   *
   * @returns The layer and the door, or null when none is chosen or the chosen door has left the layer.
   */
  public get inHand(): { readonly layer: DoorLayer; readonly door: DoorDefinition } | null {
    const layer = this.layer;
    const door = layer === null || this.chosen === null || this.chosen.layerId !== layer.id ? undefined : layer.doors.find((candidate) => candidate.id === this.chosen?.doorId);
    return layer === null || door === undefined ? null : { layer, door };
  }

  /**
   * Chooses the door in hand, the Doors palette's row, or none.
   *
   * @param layerId The door layer.
   * @param doorId The door's id, or null for none.
   * @throws Error when the layer holds no such door.
   */
  public choose(layerId: string, doorId: string | null): void {
    if (doorId !== null && !this.doorsOf(layerId).some((door) => door.id === doorId)) {
      throw new Error(`door layer ${layerId} lists no door ${doorId}`);
    }
    this.chosen = doorId === null ? null : { layerId, doorId };
    this.host.notify();
  }

  /**
   * The cells a door paints on its layer, in collision cells.
   *
   * @param layerId The door layer.
   * @param doorId The door.
   * @returns The cell indices.
   */
  public cellsOf(layerId: string, doorId: string): number[] {
    const layer = this.doorLayer(layerId);
    const value = layer.doors.findIndex((door) => door.id === doorId) + 1;
    const out: number[] = [];
    if (value === 0) {
      return out;
    }
    for (let i = 0; i < layer.cells.length; i += 1) {
      if (layer.cells[i] === value) {
        out.push(i);
      }
    }
    return out;
  }

  /**
   * Why a door may not be added to a layer, or null.
   *
   * @param layerId The door layer.
   * @returns The reason, or null.
   */
  public addRefusal(layerId: string): string | null {
    return this.doorLayer(layerId).doors.length >= MAX_DOORS_PER_LAYER ? `A door layer lists at most ${MAX_DOORS_PER_LAYER} doors` : null;
  }

  /**
   * Adds a door, unpainted, to a layer as one command and puts it in hand. Fields not named take the schema's
   * defaults, so a new door starts on the game's safe setting.
   *
   * @param layerId The door layer.
   * @param made The name, destination and fields.
   * @returns The new door's id.
   * @throws Error when the layer is not a door layer, holds the most doors, or the command was not taken.
   */
  public add(layerId: string, made: NewDoor): string {
    const layer = this.doorLayer(layerId);
    const refusal = this.addRefusal(layerId);
    if (refusal !== null) {
      throw new Error(refusal);
    }
    const mark = this.host.idMark();
    const door: DoorDefinition = { id: this.host.allocateId("door"), name: made.name.trim(), to: { map: made.to.map, arrival: made.to.arrival }, fields: { ...doorDefaults(this.host.doorFields()), ...made.fields } };
    if (!this.host.run({ kind: "addDoor", mapId: this.host.map().id, layerId, index: layer.doors.length, door })) {
      this.host.rewindIds(mark);
      throw new Error("the door was not added");
    }
    this.chosen = { layerId, doorId: door.id };
    this.host.notify();
    return door.id;
  }

  /**
   * Changes a door's name, destination or fields as one command; what a patch leaves out stays.
   *
   * @param layerId The door layer.
   * @param doorId The door.
   * @param patch The name, destination and fields to set; `fields` replaces the door's fields whole.
   * @returns False when nothing changed, and nothing was recorded.
   * @throws Error when the layer or the door is missing.
   */
  public update(layerId: string, doorId: string, patch: { readonly name?: string; readonly to?: DoorTarget; readonly fields?: { readonly [key: string]: JsonValue } }): boolean {
    const door = this.doorsOf(layerId).find((candidate) => candidate.id === doorId);
    if (door === undefined) {
      throw new Error(`door layer ${layerId} lists no door ${doorId}`);
    }
    if (patch.name !== undefined && patch.name.trim() === "") {
      throw new Error("Name the door");
    }
    const before = doorState(door);
    const after = { name: patch.name === undefined ? before.name : patch.name.trim(), to: patch.to === undefined ? before.to : { map: patch.to.map, arrival: patch.to.arrival }, fields: patch.fields ?? before.fields };
    if (JSON.stringify(before) === JSON.stringify(after)) {
      return false;
    }
    return this.host.run({ kind: "updateDoor", mapId: this.host.map().id, layerId, doorId, before, after });
  }

  /**
   * Removes a door and clears its painted cells as one command that one undo restores whole.
   *
   * @param layerId The door layer.
   * @param doorId The door.
   * @returns True once removed.
   * @throws Error when the layer or the door is missing.
   */
  public remove(layerId: string, doorId: string): boolean {
    const layer = this.doorLayer(layerId);
    const index = layer.doors.findIndex((door) => door.id === doorId);
    if (index < 0) {
      throw new Error(`door layer ${layerId} lists no door ${doorId}`);
    }
    const mapId = this.host.map().id;
    const clear: DoorCellChange[] = this.cellsOf(layerId, doorId).map((cell) => ({ index: cell, before: doorId, after: null }));
    const commands: Command[] = [...(clear.length === 0 ? [] : [{ kind: "paintDoor" as const, mapId, layerId, cells: clear }]), { kind: "removeDoor", mapId, layerId, index, door: layer.doors[index] as DoorDefinition }];
    const done = this.host.run(commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands });
    if (done && this.chosen?.doorId === doorId) {
      this.chosen = null;
      this.host.notify();
    }
    return done;
  }

  /**
   * Paints cells of a door layer with the door in hand, or erases them, as one command.
   *
   * @param layerId The door layer.
   * @param indices Cell indices in the collision grid.
   * @param erase True to erase to unpainted, the right button's stroke.
   * @returns False when nothing changed, or when a paint has no door in hand.
   * @throws Error when the layer is not a door layer or an index lies outside the map.
   */
  public paintCells(layerId: string, indices: readonly number[], erase: boolean): boolean {
    const layer = this.doorLayer(layerId);
    const hand = this.inHand;
    if (!erase && (hand === null || hand.layer.id !== layerId)) {
      return false;
    }
    const after = erase ? null : (hand?.door.id ?? null);
    const cells: DoorCellChange[] = [];
    const seen = new Set<number>();
    for (const index of indices) {
      if (!Number.isInteger(index) || index < 0 || index >= layer.cells.length) {
        throw new Error(`cell index ${index} is outside the grid of ${layer.cells.length}`);
      }
      if (seen.has(index)) {
        continue;
      }
      seen.add(index);
      const before = doorAt(layer, index)?.id ?? null;
      if (before !== after) {
        cells.push({ index, before, after });
      }
    }
    return cells.length > 0 && this.host.run({ kind: "paintDoor", mapId: this.host.map().id, layerId, cells });
  }

  /**
   * Whether the destination of a painted door holds no door back to this map, so "Make the way back" is offered
   * (ADR-122 decision 2.4).
   *
   * @param layerId The door layer.
   * @param doorId The door.
   * @returns True when the door is painted, leads to a map that exists and has no door back.
   */
  public wayBackOffered(layerId: string, doorId: string): boolean {
    const world = this.host.document();
    const door = this.doorsOf(layerId).find((candidate) => candidate.id === doorId);
    if (door === undefined || this.host.arrivalType() === undefined || !world.maps.some((map) => map.id === door.to.map)) {
      return false;
    }
    return this.cellsOf(layerId, doorId).length > 0 && !hasWayBack(world, this.host.map().id, doorId);
  }

  /**
   * Why the way back cannot be made as chosen, or null: the command is built and applied to a copy of the world,
   * and its refusal returned. Nothing is recorded.
   *
   * @param layerId The door layer.
   * @param doorId The door.
   * @param choice The sides and the new arrival point's fields.
   * @returns The reason, or null.
   */
  public wayBackRefusal(layerId: string, doorId: string, choice: WayBackChoice): string | null {
    const mark = this.host.idMark();
    try {
      this.wayBackOf(layerId, doorId, choice);
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    } finally {
      this.host.rewindIds(mark);
    }
  }

  /**
   * Makes the way back from a painted door as one command that one undo takes back whole (`wayBackCommand`): an
   * arrival point a tile outside the door, a door layer on the destination when it has none, and a one-cell door
   * beside the destination's arrival point leading to the new arrival point, carrying a copy of this door's fields.
   *
   * @param layerId The door layer.
   * @param doorId The door.
   * @param choice The sides and the new arrival point's fields.
   * @returns The way back's door id on the destination.
   * @throws Error with the refusal's words when the way back cannot be made, or the command was not taken.
   */
  public makeWayBack(layerId: string, doorId: string, choice: WayBackChoice): string {
    const mark = this.host.idMark();
    const made = this.wayBackOf(layerId, doorId, choice);
    if (!this.host.run(made.command)) {
      this.host.rewindIds(mark);
      throw new Error("the way back was not made");
    }
    return made.doorId;
  }

  /**
   * The way back's command and its door's id.
   *
   * @param layerId The door layer.
   * @param doorId The door.
   * @param choice The choice.
   * @returns The command and the new door's id.
   * @throws Error when the game names no arrival type or the way back cannot be built.
   */
  private wayBackOf(layerId: string, doorId: string, choice: WayBackChoice): { readonly command: Command; readonly doorId: string } {
    const arrivalType = this.host.arrivalType();
    if (arrivalType === undefined) {
      throw new Error("The game names no arrival point, so a way back cannot be made");
    }
    this.doorLayer(layerId);
    const ids = {
      arrival: this.host.allocateId("entity"),
      door: this.host.allocateId("door"),
      markerLayer: this.host.allocateId("layer"),
      doorLayer: this.host.allocateId("layer"),
    };
    const command = wayBackCommand(
      this.host.document(),
      {
        mapId: this.host.map().id,
        doorId,
        outside: choice.outside,
        behind: choice.behind,
        arrival: { id: ids.arrival, fields: choice.arrivalFields },
        door: { id: ids.door, name: choice.doorName.trim() },
        markerLayer: { id: ids.markerLayer, name: "Arrival points" },
        doorLayer: { id: ids.doorLayer, name: "Doors" },
      },
      arrivalType,
    );
    return { command, doorId: ids.door };
  }

  /**
   * The doors a layer lists.
   *
   * @param layerId The door layer.
   * @returns The doors.
   * @throws Error when the layer is not a door layer of the map in view.
   */
  private doorsOf(layerId: string): readonly DoorDefinition[] {
    return this.doorLayer(layerId).doors;
  }

  /**
   * A door layer of the map in view.
   *
   * @param layerId The layer.
   * @returns The layer.
   * @throws Error when it is missing or not a door layer.
   */
  private doorLayer(layerId: string): DoorLayer {
    const layer = this.host.map().layers.find((candidate) => candidate.id === layerId);
    if (layer === undefined || layer.kind !== "door") {
      throw new Error(`layer ${layerId} is not a door layer`);
    }
    return layer;
  }
}
